/**
 * Mapa do Brasil (choropleth) usando ECharts
 */
import { useEffect, useState, useCallback } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts/core';
import type { EChartsOption } from 'echarts';
import { cn } from '@/lib/utils';
import type { BrazilUF } from '@claro/shared';

interface UfData {
  uf: string;
  total: number;
  regular: number;
  emAndamento: number;
  irregular: number;
  regularConjunto?: number;
  naoRegularConjunto?: number;
  alvaraRegular?: number;
  alvaraNaoRegular?: number;
  avcbRegular?: number;
  avcbNaoRegular?: number;
}

interface BrazilMapProps {
  data: UfData[];
  selectedUF?: BrazilUF | null;
  onUFClick?: (uf: BrazilUF | null) => void;
  regularityType?: 'conjunto' | 'alvara' | 'avcb';
  title?: string;
  isLoading?: boolean;
  className?: string;
}

// Mapeamento de sigla UF para nome no GeoJSON
const UF_NAMES: Record<string, string> = {
  AC: 'Acre',
  AL: 'Alagoas',
  AM: 'Amazonas',
  AP: 'Amapá',
  BA: 'Bahia',
  CE: 'Ceará',
  DF: 'Distrito Federal',
  ES: 'Espírito Santo',
  GO: 'Goiás',
  MA: 'Maranhão',
  MG: 'Minas Gerais',
  MS: 'Mato Grosso do Sul',
  MT: 'Mato Grosso',
  PA: 'Pará',
  PB: 'Paraíba',
  PE: 'Pernambuco',
  PI: 'Piauí',
  PR: 'Paraná',
  RJ: 'Rio de Janeiro',
  RN: 'Rio Grande do Norte',
  RO: 'Rondônia',
  RR: 'Roraima',
  RS: 'Rio Grande do Sul',
  SC: 'Santa Catarina',
  SE: 'Sergipe',
  SP: 'São Paulo',
  TO: 'Tocantins',
};

// Inverso: nome para sigla
const NAME_TO_UF: Record<string, string> = Object.fromEntries(
  Object.entries(UF_NAMES).map(([uf, name]) => [name, uf])
);

export function BrazilMap({
  data,
  selectedUF,
  onUFClick,
  regularityType = 'conjunto',
  title,
  isLoading = false,
  className,
}: BrazilMapProps) {
  const [geoJsonLoaded, setGeoJsonLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Carrega GeoJSON do Brasil
  useEffect(() => {
    async function loadGeoJson() {
      try {
        const response = await fetch('/data/maps/brazil.json');
        if (!response.ok) throw new Error('Falha ao carregar mapa');
        
        const geoJson = await response.json();
        
        // Normaliza: garante que features tenham properties.name com a sigla do UF
        if (Array.isArray(geoJson.features)) {
          geoJson.features.forEach((f: any) => {
            if (f?.properties) {
              // Usa SIGLA ou Sigla como name se existir
              if (f.properties.SIGLA) {
                f.properties.name = f.properties.SIGLA;
              } else if (f.properties.Sigla) {
                f.properties.name = f.properties.Sigla;
              }
            }
          });
        }
        
        echarts.registerMap('brazil', geoJson);
        setGeoJsonLoaded(true);
      } catch (err) {
        console.error('Erro ao carregar GeoJSON:', err);
        setError('Erro ao carregar mapa');
      }
    }

    loadGeoJson();
  }, []);

  // Prepara dados para o mapa (garante que data é um array)
  // O GeoJSON usa a sigla do UF como name (ex: 'SP', 'RJ', etc)
  const safeData = Array.isArray(data) ? data : [];
  const regularityLabel = regularityType === 'alvara'
    ? 'Alvará'
    : regularityType === 'avcb'
      ? 'AVCB'
      : 'AVCB + Alvará';
  const mapTitle = title || (regularityType === 'conjunto'
    ? 'Regularidade por UF'
    : `Regularidade ${regularityLabel} por UF`);

  const mapData = safeData.map((item) => {
    // Usa a sigla diretamente como name (para match com GeoJSON)
    const name = item.uf;

    const regularValue = regularityType === 'alvara'
      ? (item.alvaraRegular ?? 0)
      : regularityType === 'avcb'
        ? (item.avcbRegular ?? 0)
        : (item.regularConjunto ?? 0);
    const nonRegularValue = regularityType === 'alvara'
      ? (item.alvaraNaoRegular ?? Math.max(0, item.total - regularValue))
      : regularityType === 'avcb'
        ? (item.avcbNaoRegular ?? Math.max(0, item.total - regularValue))
        : (item.naoRegularConjunto ?? Math.max(0, item.total - regularValue));

    return {
      name,
      value: regularValue,
      total: item.total,
      regularValue,
      nonRegularValue,
      irregular: item.irregular,
      emAndamento: item.emAndamento,
      regular: item.regular,
      fullName: UF_NAMES[item.uf] || item.uf, // Para tooltip
    };
  });

  // Encontra maximo para escala (quantidade de unidades regulares)
  const maxValue = Math.max(...mapData.map((d) => d.value), 1);

  const handleMapClick = useCallback((params: any) => {
    if (!onUFClick) return;
    
    // params.name agora é a sigla do UF (ex: 'SP')
    const ufSigla = params.name?.toUpperCase();
    if (ufSigla && UF_NAMES[ufSigla]) {
      // Toggle: se clicar no mesmo estado, desmarca
      if (selectedUF === ufSigla) {
        onUFClick(null);
      } else {
        onUFClick(ufSigla as BrazilUF);
      }
    }
  }, [onUFClick, selectedUF]);

  const option: EChartsOption = {
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        const d = params.data || {};
        const fullName = d.fullName || UF_NAMES[params.name] || params.name;
        return `
          <div style="font-weight: bold; margin-bottom: 4px;">${fullName} (${params.name})</div>
          <div style="color: #10b981;">Regulares (${regularityLabel}): <strong>${(d.regularValue || 0).toLocaleString('pt-BR')}</strong></div>
          <div>Total de unidades: ${(d.total || 0).toLocaleString('pt-BR')}</div>
          <div style="color: #94a3b8;">Não regulares (${regularityLabel}): ${(d.nonRegularValue || 0).toLocaleString('pt-BR')}</div>
        `;
      },
    },
    visualMap: {
      show: true,
      type: 'continuous',
      min: 0,
      max: maxValue,
      text: ['Mais regulares', 'Menos regulares'],
      calculable: false,
      orient: 'horizontal',
      left: 'center',
      bottom: 10,
      itemWidth: 12,
      itemHeight: 100,
      inRange: {
        color: ['#e5f9f0', '#34d399', '#047857'],
      },
      textStyle: {
        fontSize: 10,
      },
    },
    series: [
      {
        name: 'Unidades',
        type: 'map',
        map: 'brazil',
        roam: false,
        zoom: 1.1,
        aspectScale: 1,
        selectedMode: 'single',
        select: {
          itemStyle: {
            areaColor: '#3b82f6',
            borderColor: '#1d4ed8',
            borderWidth: 2,
          },
          label: {
            show: true,
            color: '#fff',
          },
        },
        emphasis: {
          itemStyle: {
            areaColor: '#60a5fa',
            borderColor: '#2563eb',
          },
          label: {
            show: true,
            color: '#000',
          },
        },
        itemStyle: {
          borderColor: '#cbd5e1',
          borderWidth: 0.5,
        },
        label: {
          show: false,
        },
        data: mapData.map((d) => ({
          ...d,
          selected: selectedUF === d.name,
        })),
      },
    ],
  };

  if (isLoading || !geoJsonLoaded) {
    return (
      <div className={cn(
        'bg-card rounded-lg border p-4 animate-pulse',
        className
      )}>
        <div className="h-4 bg-muted rounded w-32 mb-4" />
        <div className="aspect-[4/5] bg-muted rounded" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn(
        'bg-card rounded-lg border p-4 flex items-center justify-center',
        className
      )}>
        <span className="text-destructive text-sm">{error}</span>
      </div>
    );
  }

  const totalRegularByType = safeData.reduce((sum, item) => {
    if (regularityType === 'alvara') return sum + (item.alvaraRegular ?? 0);
    if (regularityType === 'avcb') return sum + (item.avcbRegular ?? 0);
    return sum + (item.regularConjunto ?? 0);
  }, 0);
  const selectedUfData = selectedUF ? safeData.find(item => item.uf === selectedUF) : null;
  const selectedUfRegularCount = selectedUfData
    ? regularityType === 'alvara'
      ? (selectedUfData.alvaraRegular ?? 0)
      : regularityType === 'avcb'
        ? (selectedUfData.avcbRegular ?? 0)
        : (selectedUfData.regularConjunto ?? 0)
    : 0;

  return (
    <div className={cn(
      'bg-card rounded-lg border p-4 h-full flex flex-col',
      className
    )}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-medium text-muted-foreground">
            {mapTitle}
          </h3>
          <span className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground">
            {totalRegularByType.toLocaleString('pt-BR')} regulares ({regularityLabel})
          </span>
        </div>
        {selectedUF && (
          <button
            onClick={() => onUFClick?.(null)}
            className="flex items-center gap-1 text-xs text-primary hover:underline bg-primary/10 px-2 py-1 rounded"
          >
            <span>{selectedUF}</span>
            {selectedUfData && (
              <span className="text-muted-foreground">({selectedUfRegularCount.toLocaleString('pt-BR')} regulares)</span>
            )}
            <span className="ml-1">×</span>
          </button>
        )}
      </div>
      
      <div className="flex-1 min-h-0">
        <ReactECharts
          option={option}
          style={{ height: '100%', width: '100%' }}
          opts={{ renderer: 'canvas' }}
          onEvents={{
            click: handleMapClick,
          }}
        />
      </div>
    </div>
  );
}
